import { useQuery, useReactiveVar } from '@apollo/client'
import { GetProjectNoteContentItemIdsQuery } from 'api/graphQL/queries/project.queries'
import {
    setElementLevelNotesCount,
    setElementNotesCount,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import {
    TASK_STATE_KEY,
    syncTaskContentItemIds,
} from 'api/graphQL/reactiveVars/workspaceTasks'
import { ProjectElementNodeWithMetadata } from 'api/graphQL/types/projectElement.types'
import { useEffect } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'

interface UseProjectTasksSyncOptions {
    projectId?: string
    selectedProjectNode?: ProjectElementNodeWithMetadata | null
    isLoading?: boolean
}

interface UseProjectTasksSyncResult {
    isProjectTasksEnabled: boolean
    taskCount: number
}

/** Syncs the task-indicator content-item IDs and exposes the badge count for the Tasks button. */
export const useProjectTasksSync = ({
    projectId,
    selectedProjectNode,
    isLoading,
}: UseProjectTasksSyncOptions): UseProjectTasksSyncResult => {
    const { previewFeatures } = usePreviewFeatures()
    const isProjectTasksEnabled = previewFeatures.hasProjectTasks
    const rawTaskCount = useReactiveVar(workspaceSelectedElement).elementNotesCount
    const selectedElementId = selectedProjectNode?.elementId
    const isInProject = selectedProjectNode?.isInProject

    const { data: noteCountData, variables: noteCountVariables, loading: noteCountLoading } = useQuery<{
        getProject: { projectNotes: { id: string; contentItemId?: string }[] }
    }>(GetProjectNoteContentItemIdsQuery, {
        variables: {
            projectId,
            projectNoteSearchInput: {
                filter: isInProject && selectedElementId ? { elementId: selectedElementId } : {},
                limit: 500,
                skip: 0,
            },
        },
        skip: !isProjectTasksEnabled || !projectId || !!isLoading || !selectedProjectNode || (!!isInProject && !selectedElementId),
        fetchPolicy: 'network-only',
        // Required so Apollo re-renders on loading→false transition; without it, a result
        // deeply equal to the previous one is suppressed and the loading guard never clears.
        notifyOnNetworkStatusChange: true,
    })

    useEffect(() => {
        if (!noteCountData) return
        // Guard: in network-only mode, `data` lags behind `variables` while loading.
        // Writing counts while they're mismatched produces stale scope (bug 2844093).
        if (noteCountLoading) return
        const notes = noteCountData.getProject?.projectNotes ?? []
        const wasElementScoped = !!noteCountVariables?.projectNoteSearchInput?.filter?.elementId
        if (wasElementScoped) {
            syncTaskContentItemIds(notes)
            setElementNotesCount(notes.length)
            setElementLevelNotesCount(notes.filter((n) => !n.contentItemId).length)
        } else {
            // Not-in-project: show project-total count; no element-level indicator dots.
            setElementNotesCount(notes.length)
            setElementLevelNotesCount(0)
        }
    }, [noteCountData, noteCountVariables, noteCountLoading])

    // Clear on element change. Keyed on scalars so PT activation (same element, new object)
    // doesn't zero a valid count. Guarded so the shared elementNotesCount reactive var
    // (also written by WorkspaceContentArea) isn't cleared for non-preview-flag users.
    useEffect(() => {
        if (!isProjectTasksEnabled) return
        syncTaskContentItemIds([])
        setElementNotesCount(0)
        setElementLevelNotesCount(0)
    }, [isProjectTasksEnabled, selectedElementId, isInProject])

    // Listens for task CRUD broadcasts from the popup window.
    useEffect(() => {
        if (!isProjectTasksEnabled || !projectId) return
        const onStorage = (e: StorageEvent) => {
            if (e.key !== TASK_STATE_KEY) return
            try {
                const payload = e.newValue ? JSON.parse(e.newValue) : null
                if (payload?.projectId !== projectId) return
                const isProjectLevelBroadcast = payload.elementId === ''
                const isElementMatch = isInProject
                    ? payload.elementId === selectedElementId
                    : isProjectLevelBroadcast
                if (!isProjectLevelBroadcast && !isElementMatch) return
                const ids: string[] = Array.isArray(payload.contentItemIds)
                    ? payload.contentItemIds
                    : []
                if (isInProject && isElementMatch) {
                    syncTaskContentItemIds(ids.map((id) => ({ contentItemId: id })))
                }
                setElementNotesCount(Number(payload.totalCount ?? 0))
                // Mirror query-path logic: not-in-project nodes never show element-level indicator dots.
                setElementLevelNotesCount(isInProject ? Number(payload.elementLevelCount ?? 0) : 0)
            } catch {
                // ignore malformed payload
            }
        }
        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }, [isProjectTasksEnabled, projectId, isInProject, selectedElementId])

    const taskCount = isLoading ? 0 : rawTaskCount

    return { isProjectTasksEnabled, taskCount }
}
